import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle, Loader2, Brain, CheckCircle2, XCircle } from 'lucide-react';

/**
 * "Tasks" card in the right sidebar — one row per task with completion
 * state, an inline answer-submission input (text/number/flag — all formats
 * accepted), an AI-hint trigger, and inline hint output.
 *
 * Hint quota label flips based on tier:
 *   - Pro       → "Get AI hint"
 *   - Free (q>0)→ "Get AI hint (N/3 left)"
 *   - Free (q=0)→ "AI hints exhausted — Upgrade"
 *   - Quota unknown → fallback "Get AI hint" until /api/ai/hint-quota loads
 */
const hintButtonLabel = (isPro, hintQuota) => {
  if (isPro) return 'Get AI hint';
  if (hintQuota?.tier === 'free') {
    const r = hintQuota.remaining;
    return r > 0
      ? `Get AI hint (${r}/${hintQuota.limit} left)`
      : 'AI hints exhausted — Upgrade';
  }
  return 'Get AI hint';
};

const TaskAnswerForm = ({ taskId, submitting, feedback, disabled, onSubmit }) => {
  const [value, setValue] = useState('');

  const handle = async (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    const ok = await onSubmit(taskId, v);
    if (ok) setValue(''); // clear on success only
  };

  return (
    <form
      onSubmit={handle}
      className="mt-3"
      data-testid={`task-answer-form-${taskId}`}
    >
      <div className="flex gap-2">
        <Input
          data-testid={`task-answer-input-${taskId}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Your answer (text, number, or flag{...})"
          disabled={disabled || submitting}
          className="bg-[#0A0A0A] border-gray-800 text-white text-xs font-mono h-8 placeholder:text-gray-600"
        />
        <Button
          data-testid={`task-answer-submit-${taskId}`}
          type="submit"
          size="sm"
          disabled={disabled || submitting || !value.trim()}
          className="h-8 px-3 bg-[#00FF00] text-black hover:bg-[#00DD00] text-xs font-semibold"
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Submit'}
        </Button>
      </div>
      {disabled && (
        <p className="mt-1.5 text-[10px] text-gray-500">
          Click <span className="text-[#00FF00]">Start Machine</span> above to enable submission.
        </p>
      )}
      {feedback && (
        <div
          data-testid={`task-answer-feedback-${taskId}`}
          className={`mt-2 flex items-start gap-1.5 text-xs ${
            feedback.type === 'success' ? 'text-[#00FF00]' : 'text-[#FF0055]'
          }`}
        >
          {feedback.type === 'success' ? (
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          ) : (
            <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          )}
          <span>{feedback.message}</span>
        </div>
      )}
    </form>
  );
};

const TaskRow = ({
  task,
  done,
  hint,
  loadingHint,
  isPro,
  hintQuota,
  onRequestHint,
  answerFeedback,
  answerSubmitting,
  answerDisabled,
  onSubmitAnswer,
}) => (
  <div
    data-testid={`task-row-${task.id}`}
    className={`border rounded-lg p-3 ${
      done ? 'border-[#00FF00]/50 bg-[#00FF00]/5' : 'border-gray-800'
    }`}
  >
    <div className="flex items-start gap-2">
      {done ? (
        <CheckCircle className="w-5 h-5 text-[#00FF00] flex-shrink-0 mt-0.5" />
      ) : (
        <div className="w-5 h-5 rounded-full border-2 border-gray-600 flex-shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-white mb-1">{task.title}</h4>
        <p className="text-xs text-gray-400">{task.description || task.question}</p>

        {!done && (
          <>
            <TaskAnswerForm
              taskId={task.id}
              submitting={answerSubmitting}
              feedback={answerFeedback}
              disabled={answerDisabled}
              onSubmit={onSubmitAnswer}
            />
            <Button
              data-testid={`task-hint-btn-${task.id}`}
              size="sm"
              variant="outline"
              disabled={loadingHint}
              onClick={() => onRequestHint(task.id)}
              className="mt-2 h-7 text-xs border-[#00BFFF]/40 text-[#00BFFF] hover:bg-[#00BFFF]/10"
            >
              {loadingHint ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <>
                  <Brain className="w-3 h-3 mr-1" />
                  {hintButtonLabel(isPro, hintQuota)}
                </>
              )}
            </Button>
          </>
        )}

        {hint && (
          <div
            data-testid={`task-hint-output-${task.id}`}
            className="mt-2 p-2 rounded bg-[#0A0A0A] border border-[#00BFFF]/30 text-xs text-gray-300 whitespace-pre-wrap"
          >
            <span className="text-[#00BFFF] font-semibold">AI hint: </span>
            {hint}
          </div>
        )}
      </div>
    </div>
  </div>
);

export const RoomTasksList = ({
  tasks,
  completedTasks,
  taskHints,
  hintLoadingId,
  isPro,
  hintQuota,
  onRequestHint,
  taskFeedback,
  taskSubmittingId,
  answerDisabled,
  onSubmitAnswer,
}) => (
  <Card className="bg-[#1A1A1A] border-gray-800" data-testid="room-tasks-card">
    <CardHeader>
      <CardTitle className="text-white text-sm">Tasks</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {tasks.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          done={completedTasks.includes(task.id)}
          hint={taskHints[task.id]}
          loadingHint={hintLoadingId === task.id}
          isPro={isPro}
          hintQuota={hintQuota}
          onRequestHint={onRequestHint}
          answerFeedback={taskFeedback[task.id]}
          answerSubmitting={taskSubmittingId === task.id}
          answerDisabled={answerDisabled}
          onSubmitAnswer={onSubmitAnswer}
        />
      ))}
    </CardContent>
  </Card>
);
